import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  Save,
  Loader2,
  Check,
  AlertCircle,
  HelpCircle,
  Trash2
} from 'lucide-react';
import { AdvancedFilterOptions, FilterPreset } from '../../../shared/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  saveFilterPreset,
  loadFilterPresets,
  deleteFilterPreset,
  applyFilterPreset,
  isFilterPresetNameTaken
} from '@/lib/filter-presets';

interface AdvancedFiltersProps {
  filters: AdvancedFilterOptions;
  onFiltersChange: (filters: AdvancedFilterOptions) => void;
  onClearFilters: () => void;
  className?: string;
}

const POPULAR_TLDS = [
  '.com', '.net', '.org', '.io', '.ai', '.co', '.me', '.app', '.dev',
  '.tech', '.online', '.site', '.info', '.biz', '.name'
];

const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({
  filters,
  onFiltersChange,
  onClearFilters,
  className
}) => {
  const [presets, setPresets] = useState<FilterPreset[]>([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [regexError, setRegexError] = useState<string | null>(null);
  const [booleanError, setBooleanError] = useState<string | null>(null);

  // Load presets on mount
  useEffect(() => {
    setPresets(loadFilterPresets());
  }, []);

  // Validate regex pattern
  const validateRegex = (pattern: string): boolean => {
    if (!pattern) return true;
    try {
      new RegExp(pattern);
      setRegexError(null);
      return true;
    } catch (error) {
      setRegexError('Invalid regex pattern');
      return false;
    }
  };

  // Validate boolean query
  const validateBooleanQuery = (query: string): boolean => {
    if (!query) return true;

    // Basic validation for boolean operators
    const allowedPattern = /^[\w\s&|!()]+$/;
    if (!allowedPattern.test(query)) {
      setBooleanError('Invalid characters in boolean query');
      return false;
    }

    // Check for balanced parentheses
    let parenCount = 0;
    for (const char of query) {
      if (char === '(') parenCount++;
      if (char === ')') parenCount--;
      if (parenCount < 0) {
        setBooleanError('Unbalanced parentheses');
        return false;
      }
    }
    if (parenCount !== 0) {
      setBooleanError('Unbalanced parentheses');
      return false;
    }

    setBooleanError(null);
    return true;
  };

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.search) count++;
    if (filters.regex) count++;
    if (filters.booleanQuery) count++;
    if (filters.tlds && filters.tlds.length > 0) count++;
    if (filters.lengthMin !== undefined || filters.lengthMax !== undefined) count++;
    if (filters.valuationMin !== undefined || filters.valuationMax !== undefined) count++;
    if (filters.priceMin !== undefined || filters.priceMax !== undefined) count++;
    if (filters.excludeUnavailablePricing) count++;
    if (filters.premiumOnly) count++;
    if (filters.purchasableOnly) count++;
    if (filters.registrar) count++;
    if (filters.dateFrom || filters.dateTo) count++;
    return count;
  }, [filters]);

  // Handle filter changes - apply immediately
  const updateFilter = (key: keyof AdvancedFilterOptions, value: any) => {
    const newFilters = { ...filters, [key]: value };
    onFiltersChange(newFilters);
  };

  // Handle TLD selection
  const toggleTLD = (tld: string) => {
    const currentTlds = filters.tlds || [];
    const newTlds = currentTlds.includes(tld)
      ? currentTlds.filter(t => t !== tld)
      : [...currentTlds, tld];
    updateFilter('tlds', newTlds);
  };

  // Handle regex change with validation
  const handleRegexChange = (value: string) => {
    updateFilter('regex', value);
    validateRegex(value);
  };

  // Handle boolean query change with validation
  const handleBooleanQueryChange = (value: string) => {
    updateFilter('booleanQuery', value);
    validateBooleanQuery(value);
  };

  // Save current filters as preset
  const handleSavePreset = async () => {
    if (!presetName.trim()) return;

    if (isFilterPresetNameTaken(presetName.trim())) {
      alert('A preset with this name already exists');
      return;
    }

    setIsSaving(true);
    try {
      const newPreset = saveFilterPreset(presetName.trim(), filters);
      setPresets(prev => [...prev, newPreset]);
      setPresetName('');
      setShowSaveModal(false);
    } catch (error) {
      alert('Failed to save preset');
    } finally {
      setIsSaving(false);
    }
  };

  // Apply preset
  const handleApplyPreset = (presetId: string) => {
    const preset = applyFilterPreset(presetId);
    if (preset) {
      onFiltersChange(preset);
    }
  };

  // Delete preset
  const handleDeletePreset = (presetId: string) => {
    if (confirm('Are you sure you want to delete this preset?')) {
      deleteFilterPreset(presetId);
      setPresets(prev => prev.filter(p => p.id !== presetId));
    }
  };

  return (
    <div className={cn("space-y-6", className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Advanced Search & Filters</h3>
        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="text-slate-500 hover:text-slate-700"
          >
            <X className="h-4 w-4 mr-1" />
            Clear All
          </Button>
        )}
      </div>

      {/* Filters Content */}
      <div className="space-y-6">
            {/* Search Methods */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Regex Search */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center">
                  Regex Pattern
                  <HelpCircle className="h-3 w-3 ml-1 text-slate-400" />
                </label>
                <div className="relative">
                  <Input
                    placeholder="^tech.*"
                    value={filters.regex || ''}
                    onChange={(e) => handleRegexChange(e.target.value)}
                    className={cn(
                      "pr-8",
                      regexError && "border-red-500 focus:border-red-500"
                    )}
                  />
                  {filters.regex && (
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                      {regexError ? (
                        <AlertCircle className="h-4 w-4 text-red-500" />
                      ) : (
                        <Check className="h-4 w-4 text-green-500" />
                      )}
                    </div>
                  )}
                </div>
                {regexError && (
                  <p className="text-xs text-red-500">{regexError}</p>
                )}
              </div>

              {/* Boolean Search */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center">
                  Boolean Search
                  <HelpCircle className="h-3 w-3 ml-1 text-slate-400" />
                </label>
                <div className="relative">
                  <Input
                    placeholder="tech AND startup"
                    value={filters.booleanQuery || ''}
                    onChange={(e) => handleBooleanQueryChange(e.target.value)}
                    className={cn(
                      "pr-8",
                      booleanError && "border-red-500 focus:border-red-500"
                    )}
                  />
                  {filters.booleanQuery && (
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                      {booleanError ? (
                        <AlertCircle className="h-4 w-4 text-red-500" />
                      ) : (
                        <Check className="h-4 w-4 text-green-500" />
                      )}
                    </div>
                  )}
                </div>
                {booleanError && (
                  <p className="text-xs text-red-500">{booleanError}</p>
                )}
              </div>
            </div>

            {/* TLD Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Top-Level Domains</label>
              <div className="flex flex-wrap gap-2">
                {POPULAR_TLDS.map(tld => (
                  <Button
                    key={tld}
                    variant={filters.tlds?.includes(tld) ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleTLD(tld)}
                    className="h-8"
                  >
                    {tld}
                  </Button>
                ))}
              </div>
            </div>

            {/* Range Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Length Range */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Domain Length</label>
                <div className="flex items-center space-x-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={filters.lengthMin || ''}
                    onChange={(e) => updateFilter('lengthMin', e.target.value ? parseInt(e.target.value) : undefined)}
                    className="w-20"
                    min="1"
                  />
                  <span className="text-slate-500">to</span>
                  <Input
                    type="number"
                    placeholder="Max"
                    value={filters.lengthMax || ''}
                    onChange={(e) => updateFilter('lengthMax', e.target.value ? parseInt(e.target.value) : undefined)}
                    className="w-20"
                    min="1"
                  />
                  <span className="text-sm text-slate-500">characters</span>
                </div>
              </div>

              {/* Valuation Range */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Valuation Range</label>
                <div className="flex items-center space-x-2">
                  <Input
                    type="number"
                    placeholder="Min $"
                    value={filters.valuationMin || ''}
                    onChange={(e) => updateFilter('valuationMin', e.target.value ? parseFloat(e.target.value) : undefined)}
                    className="w-24"
                    min="0"
                  />
                  <span className="text-slate-500">to</span>
                  <Input
                    type="number"
                    placeholder="Max $"
                    value={filters.valuationMax || ''}
                    onChange={(e) => updateFilter('valuationMax', e.target.value ? parseFloat(e.target.value) : undefined)}
                    className="w-24"
                    min="0"
                  />
                </div>
              </div>
            </div>

            {/* Pricing Filters */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300">Pricing Filters</h4>

              {/* Price Range */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Purchase Price Range</label>
                  <div className="flex items-center space-x-2">
                    <Input
                      type="number"
                      placeholder="Min $"
                      value={filters.priceMin || ''}
                      onChange={(e) => updateFilter('priceMin', e.target.value ? parseFloat(e.target.value) : undefined)}
                      className="w-24"
                      min="0"
                      step="0.01"
                    />
                    <span className="text-slate-500">to</span>
                    <Input
                      type="number"
                      placeholder="Max $"
                      value={filters.priceMax || ''}
                      onChange={(e) => updateFilter('priceMax', e.target.value ? parseFloat(e.target.value) : undefined)}
                      className="w-24"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                {/* Pricing Options */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Pricing Options</label>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={filters.excludeUnavailablePricing || false}
                        onChange={(e) => updateFilter('excludeUnavailablePricing', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        Exclude domains with unavailable pricing
                      </span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={filters.premiumOnly || false}
                        onChange={(e) => updateFilter('premiumOnly', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        Premium domains only
                      </span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={filters.purchasableOnly || false}
                        onChange={(e) => updateFilter('purchasableOnly', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        Purchasable domains only
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Registrar Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Registrar</label>
                <Input
                  placeholder="e.g., Namecheap, Name.com"
                  value={filters.registrar || ''}
                  onChange={(e) => updateFilter('registrar', e.target.value)}
                />
              </div>

              {/* Date Range */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Scan Date Range</label>
                <div className="flex items-center space-x-2">
                  <Input
                    type="date"
                    value={filters.dateFrom ? filters.dateFrom.toISOString().split('T')[0] : ''}
                    onChange={(e) => updateFilter('dateFrom', e.target.value ? new Date(e.target.value) : undefined)}
                    className="flex-1"
                  />
                  <span className="text-slate-500">to</span>
                  <Input
                    type="date"
                    value={filters.dateTo ? filters.dateTo.toISOString().split('T')[0] : ''}
                    onChange={(e) => updateFilter('dateTo', e.target.value ? new Date(e.target.value) : undefined)}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>

            {/* Preset Management */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">Saved Filter Presets</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSaveModal(true)}
                  disabled={activeFilterCount === 0}
                >
                  <Save className="h-4 w-4 mr-1" />
                  Save Current
                </Button>
              </div>

              {presets.length > 0 ? (
                <div className="space-y-2">
                  {presets.map(preset => (
                    <div key={preset.id} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium text-sm">{preset.name}</div>
                        <div className="text-xs text-slate-500">
                          {new Date(preset.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleApplyPreset(preset.id)}
                        >
                          Apply
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeletePreset(preset.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">
                  No saved presets yet. Save your current filters to create one.
                </p>
              )}
            </div>
      </div>

      {/* Save Preset Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Save Filter Preset</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">Preset Name</label>
                <Input
                  placeholder="e.g., Tech Startups"
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setShowSaveModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSavePreset}
                  disabled={!presetName.trim() || isSaving}
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-1" />
                  )}
                  Save Preset
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdvancedFilters;
